import { CurrencyPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, ResourceRef, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { ReservationListItem, ReservationListParams, ReservationListResponse } from '@models/reservations';
import { TranslatePipe } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { LocaleSwitcherService } from '@services/locale-switcher.service';
import { ReservationsService } from '@services/reservations.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { BoolComponent } from '@shared/components/bool/bool.component';
import { toDateOnly } from '@utils/date-only';
import { loadVisibleColumns, saveVisibleColumns } from '@utils/visible-columns-storage';
import { debounceTime, distinctUntilChanged, filter, map, skip } from 'rxjs';

// Column definition types

type FilterType = 'text' | 'dropdown' | 'boolean' | 'daterange' | 'numrange' | 'none';
type DropdownSource = 'sites' | 'reservationStatuses' | 'waters' | 'paymentStatuses' | 'reservationTypes';
interface ColumnDef {
  key: string;
  labelKey: string;
  filterType: FilterType;
  filterField?: string;
  filterFieldFrom?: string;
  filterFieldTo?: string;
  dropdownSource?: DropdownSource;
}

const ALL_COLUMNS: ColumnDef[] = [
  {
    key: 'reservationNumber',
    labelKey: 'reservations.field.reservationNumber',
    filterType: 'text',
    filterField: 'reservationNumber',
  },
  {
    key: 'siteName',
    labelKey: 'reservations.field.site',
    filterType: 'dropdown',
    filterField: 'siteId',
    dropdownSource: 'sites',
  },
  { key: 'customerName', labelKey: 'reservations.field.customer', filterType: 'text', filterField: 'customer' },
  {
    key: 'waterTitle',
    labelKey: 'reservations.field.water',
    filterType: 'dropdown',
    filterField: 'waterId',
    dropdownSource: 'waters',
  },
  {
    key: 'arrivalDate',
    labelKey: 'reservations.field.arrival',
    filterType: 'daterange',
    filterFieldFrom: 'arrivalFrom',
    filterFieldTo: 'arrivalTo',
  },
  {
    key: 'departureDate',
    labelKey: 'reservations.field.departure',
    filterType: 'daterange',
    filterFieldFrom: 'departureFrom',
    filterFieldTo: 'departureTo',
  },
  {
    key: 'statusName',
    labelKey: 'reservations.field.status',
    filterType: 'dropdown',
    filterField: 'statusId',
    dropdownSource: 'reservationStatuses',
  },
  {
    key: 'paymentStatus',
    labelKey: 'reservations.field.paymentStatus',
    filterType: 'dropdown',
    filterField: 'paymentStatus',
    dropdownSource: 'paymentStatuses',
  },
  {
    key: 'retailPrice',
    labelKey: 'reservations.field.retailPrice',
    filterType: 'numrange',
    filterFieldFrom: 'retailPriceMin',
    filterFieldTo: 'retailPriceMax',
  },
  {
    key: 'reservationType',
    labelKey: 'reservations.field.type',
    filterType: 'dropdown',
    filterField: 'reservationType',
    dropdownSource: 'reservationTypes',
  },
  { key: 'companyName', labelKey: 'reservations.field.companyName', filterType: 'text', filterField: 'companyName' },
  { key: 'lastMinute', labelKey: 'reservations.field.lastMinute', filterType: 'boolean', filterField: 'lastMinute' },
  { key: 'insurance', labelKey: 'reservations.field.insurance', filterType: 'boolean', filterField: 'insurance' },
  { key: 'dog', labelKey: 'reservations.field.dog', filterType: 'boolean', filterField: 'dog' },
  // 'wholeWater' has no column here any more (CS-228): the flag lives on each swim line, so one
  // true/false per reservation row would misreport a booking that rents the lake for part of its stay.
  // This list couples each filter to a visible column, so the filter went with it; the API still
  // accepts a wholeWater filter (matching reservations with any whole-water line) if it is wanted back.
  { key: 'financial', labelKey: 'reservations.field.financial', filterType: 'boolean', filterField: 'financial' },
  {
    key: 'financialNumber',
    labelKey: 'reservations.field.financialNumber',
    filterType: 'text',
    filterField: 'financialNumber',
  },
  {
    key: 'financialNumberBuy',
    labelKey: 'reservations.field.financialNumberBuy',
    filterType: 'text',
    filterField: 'financialNumberBuy',
  },
  {
    key: 'purchasePrice',
    labelKey: 'reservations.field.purchasePrice',
    filterType: 'numrange',
    filterFieldFrom: 'purchasePriceMin',
    filterFieldTo: 'purchasePriceMax',
  },
  {
    key: 'payedDownPayment',
    labelKey: 'reservations.field.payedDownPayment',
    filterType: 'boolean',
    filterField: 'payedDownPayment',
  },
  {
    key: 'downPaymentPrice',
    labelKey: 'reservations.field.downPaymentPrice',
    filterType: 'numrange',
    filterFieldFrom: 'downPaymentPriceMin',
    filterFieldTo: 'downPaymentPriceMax',
  },
  {
    key: 'payedRestPayment',
    labelKey: 'reservations.field.payedRestPayment',
    filterType: 'boolean',
    filterField: 'payedRestPayment',
  },
  {
    key: 'restPaymentPrice',
    labelKey: 'reservations.field.restPaymentPrice',
    filterType: 'numrange',
    filterFieldFrom: 'restPaymentPriceMin',
    filterFieldTo: 'restPaymentPriceMax',
  },
  {
    key: 'emergencyContact',
    labelKey: 'reservations.field.emergencyContact',
    filterType: 'text',
    filterField: 'emergencyContact',
  },
  {
    key: 'createdOn',
    labelKey: 'reservations.field.createdOn',
    filterType: 'daterange',
    filterFieldFrom: 'createdFrom',
    filterFieldTo: 'createdTo',
  },
  {
    key: 'invoiceDateOne',
    labelKey: 'reservations.field.invoiceOne',
    filterType: 'daterange',
    filterFieldFrom: 'invoiceOneFrom',
    filterFieldTo: 'invoiceOneTo',
  },
  {
    key: 'invoiceDateTwo',
    labelKey: 'reservations.field.invoiceTwo',
    filterType: 'daterange',
    filterFieldFrom: 'invoiceTwoFrom',
    filterFieldTo: 'invoiceTwoTo',
  },
  {
    key: 'cancelledOn',
    labelKey: 'reservations.field.cancelledOn',
    filterType: 'daterange',
    filterFieldFrom: 'cancelledFrom',
    filterFieldTo: 'cancelledTo',
  },
];

const DEFAULT_VISIBLE_COLUMNS = [
  'reservationNumber',
  'siteName',
  'customerName',
  'waterTitle',
  'arrivalDate',
  'departureDate',
  'statusName',
  'paymentStatus',
  'retailPrice',
];

// Static (non-codelist) dropdown option sources.
const PAYMENT_STATUS_OPTIONS = [
  { id: 'Unpaid', name: 'reservations.paymentStatus.Unpaid' },
  { id: 'DepositPaid', name: 'reservations.paymentStatus.DepositPaid' },
  { id: 'Paid', name: 'reservations.paymentStatus.Paid' },
];
const RESERVATION_TYPE_OPTIONS = [
  { id: 'private', name: 'reservations.type.private' },
  { id: 'business', name: 'reservations.type.business' },
];

const PAYMENT_STATUS_CLASSES: Record<string, string> = {
  Unpaid: 'bg-red-100 text-red-800',
  DepositPaid: 'bg-yellow-100 text-yellow-800',
  Paid: 'bg-green-100 text-green-800',
};

const FILTERS_SESSION_KEY = 'reservation-list-filters';
const COLUMNS_STORAGE_KEY = 'reservation-list-columns';

type DropdownValue = number | string | boolean | null;
type RangeValue = string | number | null;

interface PersistedFilters {
  search: string;
  textFilters: Record<string, string>;
  dropdownFilters: Record<string, DropdownValue>;
  rangeFilters: Record<string, RangeValue>;
}

// Component

@Component({
  selector: 'app-reservation-list',
  templateUrl: './reservation-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    CurrencyPipe,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TranslatePipe,
    BoolComponent,
  ],
})
export class ReservationListComponent {
  private readonly reservationsService = inject(ReservationsService);
  readonly codeListsService = inject(CodeListsService);
  private readonly localeSwitcher = inject(LocaleSwitcherService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);

  // Reference data — codelist signals + static option lists.
  readonly dropdownSources: Record<DropdownSource, () => { id: number | string; name: string }[]> = {
    sites: this.codeListsService.sites,
    reservationStatuses: this.codeListsService.reservationStatuses,
    waters: this.codeListsService.waters,
    paymentStatuses: () => PAYMENT_STATUS_OPTIONS,
    reservationTypes: () => RESERVATION_TYPE_OPTIONS,
  };

  // Translate the static option labels (codelist names are already display strings).
  readonly translatedSources: Record<DropdownSource, boolean> = {
    sites: false,
    reservationStatuses: false,
    waters: false,
    paymentStatuses: true,
    reservationTypes: true,
  };

  readonly allColumns = ALL_COLUMNS;

  // Column visibility — synced to URL query param `columns`, falls back to localStorage.
  readonly visibleColumnKeys = signal<string[]>(
    loadVisibleColumns(
      COLUMNS_STORAGE_KEY,
      this.route.snapshot.queryParamMap.get('columns'),
      ALL_COLUMNS.map((c) => c.key),
      DEFAULT_VISIBLE_COLUMNS,
    ),
  );
  readonly displayedColumns = computed(() => [...this.visibleColumnKeys(), 'actions']);
  readonly filterColumns = computed(() => this.displayedColumns().map((k) => `filter-${k}`));

  readonly page = signal(1);
  readonly pageSize = signal(25);

  // Restore persisted filters for the session (AC: remember applied filters within the session).
  private readonly persisted = ReservationListComponent.loadPersisted();

  readonly searchInput = signal(this.persisted.search);
  private readonly searchQuery = signal(this.persisted.search);

  private readonly textFilterInputs = signal<Record<string, string>>(this.persisted.textFilters);
  private readonly textFilters = signal<Record<string, string>>(this.persisted.textFilters);

  readonly dropdownFilters = signal<Record<string, DropdownValue>>(this.persisted.dropdownFilters);
  readonly rangeFilters = signal<Record<string, RangeValue>>(this.persisted.rangeFilters);

  private readonly requestParams = computed<ReservationListParams>(() => {
    const tf = this.textFilters();
    const df = this.dropdownFilters();
    const rf = this.rangeFilters();
    const entries: [string, unknown][] = [];
    for (const col of ALL_COLUMNS) {
      if (col.filterType === 'text' && col.filterField) {
        entries.push([col.filterField, tf[col.filterField] || null]);
      } else if ((col.filterType === 'dropdown' || col.filterType === 'boolean') && col.filterField) {
        entries.push([col.filterField, df[col.filterField] ?? null]);
      } else if (
        (col.filterType === 'daterange' || col.filterType === 'numrange') &&
        col.filterFieldFrom &&
        col.filterFieldTo
      ) {
        entries.push([col.filterFieldFrom, rf[col.filterFieldFrom] ?? null]);
        entries.push([col.filterFieldTo, rf[col.filterFieldTo] ?? null]);
      }
    }
    return {
      search: this.searchQuery() || null,
      page: this.page(),
      pageSize: this.pageSize(),
      locale: this.localeSwitcher.selectedLocaleCode(),
      ...Object.fromEntries(entries),
    } as ReservationListParams;
  });

  readonly reservationsResource: ResourceRef<ReservationListResponse | undefined> = rxResource({
    params: () => this.requestParams(),
    stream: ({ params }) =>
      this.reservationsService.getReservations(params).pipe(showGenericErrorToast(this.toastService, () => undefined)),
  });

  readonly reservations = toSignal(
    toObservable(this.reservationsResource.value).pipe(
      filter((v): v is ReservationListResponse => v !== undefined),
      map((v) => v.items),
    ),
    { initialValue: [] as ReservationListItem[] },
  );
  readonly totalCount = toSignal(
    toObservable(this.reservationsResource.value).pipe(
      filter((v): v is ReservationListResponse => v !== undefined),
      map((v) => v.totalCount),
    ),
    { initialValue: 0 },
  );

  constructor() {
    this.syncQueryParams();

    toObservable(this.searchInput)
      .pipe(skip(1), debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((v) => {
        this.searchQuery.set(v);
        this.page.set(1);
        this.persistFilters();
      });

    toObservable(this.textFilterInputs)
      .pipe(skip(1), debounceTime(300), takeUntilDestroyed())
      .subscribe((v) => {
        this.textFilters.set(v);
        this.page.set(1);
        this.persistFilters();
      });
  }

  // Event handlers

  onSearchInput(value: string): void {
    this.searchInput.set(value);
  }

  onTextFilterInput(filterField: string, value: string): void {
    this.textFilterInputs.update((prev) => ({ ...prev, [filterField]: value }));
  }

  onDropdownFilterChange(filterField: string, value: DropdownValue | ''): void {
    this.dropdownFilters.update((prev) => ({ ...prev, [filterField]: value === '' ? null : value }));
    this.page.set(1);
    this.persistFilters();
  }

  onRangeFilterChange(field: string, value: string, isNumber: boolean): void {
    let parsed: RangeValue;
    if (value === '') {
      parsed = null;
    } else if (isNumber) {
      const num = Number(value);
      parsed = Number.isFinite(num) ? num : null; // ignore partial/invalid input (NaN)
    } else {
      parsed = value;
    }
    this.rangeFilters.update((prev) => ({ ...prev, [field]: parsed }));
    this.page.set(1);
    this.persistFilters();
  }

  // Date filters use the Material datepicker (Date in, ISO yyyy-MM-dd string stored for the query param).
  onDateFilterChange(field: string, date: Date | null): void {
    const iso = toDateOnly(date);
    this.rangeFilters.update((prev) => ({ ...prev, [field]: iso }));
    this.page.set(1);
    this.persistFilters();
  }

  getDateFilterValue(field: string): Date | null {
    const v = this.rangeFilters()[field];
    if (typeof v !== 'string' || !v) return null;
    // Parse the yyyy-MM-dd string as a LOCAL date; new Date('yyyy-MM-dd') would parse as UTC and
    // can shift the day in non-UTC timezones.
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  clearAllFilters(): void {
    this.searchInput.set('');
    this.searchQuery.set('');
    this.textFilterInputs.set({});
    this.textFilters.set({});
    this.dropdownFilters.set({});
    this.rangeFilters.set({});
    this.page.set(1);
    this.persistFilters();
  }

  readonly hasActiveFilters = computed(
    () =>
      !!this.searchInput() ||
      Object.values(this.textFilterInputs()).some((v) => !!v) ||
      Object.values(this.dropdownFilters()).some((v) => v != null) ||
      Object.values(this.rangeFilters()).some((v) => v != null),
  );

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex + 1);
    this.pageSize.set(event.pageSize);
  }

  // Column helpers

  toggleColumn(key: string): void {
    const current = this.visibleColumnKeys();
    const updated = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    if (updated.length === 0) return;
    const ordered = ALL_COLUMNS.map((c) => c.key).filter((k) => updated.includes(k));
    this.visibleColumnKeys.set(ordered);
    saveVisibleColumns(COLUMNS_STORAGE_KEY, ordered);
    this.syncQueryParams();
  }

  private syncQueryParams(): void {
    this.router.navigate([], {
      queryParams: { columns: this.visibleColumnKeys().join(',') },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  isColumnVisible(key: string): boolean {
    return this.visibleColumnKeys().includes(key);
  }

  getDropdownFilterValue(filterField: string): DropdownValue | '' {
    const v = this.dropdownFilters()[filterField];
    return v == null ? '' : v;
  }

  getBooleanFilterValue(filterField: string): boolean | '' {
    const v = this.dropdownFilters()[filterField];
    return v == null ? '' : (v as boolean);
  }

  getTextFilterValue(filterField: string): string {
    return this.textFilterInputs()[filterField] ?? '';
  }

  getRangeFilterValue(field: string): string {
    const v = this.rangeFilters()[field];
    return v == null ? '' : String(v);
  }

  // Styling / display

  getPaymentStatusClass(status: string): string {
    return PAYMENT_STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-800';
  }

  // Session persistence

  private persistFilters(): void {
    const payload: PersistedFilters = {
      search: this.searchInput(),
      textFilters: this.textFilterInputs(),
      dropdownFilters: this.dropdownFilters(),
      rangeFilters: this.rangeFilters(),
    };
    try {
      sessionStorage.setItem(FILTERS_SESSION_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("sessionStorage unavailable (e.g. private mode) — filters simply won't persist.", e);
    }
  }

  private static loadPersisted(): PersistedFilters {
    const empty: PersistedFilters = { search: '', textFilters: {}, dropdownFilters: {}, rangeFilters: {} };
    try {
      const raw = sessionStorage.getItem(FILTERS_SESSION_KEY);
      if (!raw) return empty;
      return { ...empty, ...(JSON.parse(raw) as Partial<PersistedFilters>) };
    } catch {
      return empty;
    }
  }

  // Row actions

  navigateToCreate(): void {
    this.router.navigate(['/reservations/create'], { queryParams: { backUrl: this.router.url } });
  }

  navigateToReservation(id: number): void {
    this.router.navigate(['/reservations/edit', id], { queryParams: { backUrl: this.router.url } });
  }
}
